import canReachApi from 'shared/mixins/canReachApi'
import delay from 'shared/utils/delay'

// @vue/component
const attachments = {
	mixins: [canReachApi],
	data() {
		return {
			currentAttachments: [],
			removedAttachments: [],
			newAttachmentsByName: {},
			uploadRequest: null,
		}
	},
	computed: {
		removedAttachmentIds() {
			return this.removedAttachments.map(attachment => attachment.id)
		},
		newAttachments() {
			return Object.values(this.newAttachmentsByName)
		},
		attachments() {
			return [...this.currentAttachments, ...this.newAttachments].filter(
				attachment => attachment?.id && !this.removedAttachmentIds.includes(attachment.id)
			)
		},
		isUploadingAttachmentFiles() {
			return this.newAttachments.some(attachment => !attachment.isUploaded && !attachment.uploadFailed)
		},
		hasFailedAttachmentFileUploads() {
			return this.newAttachments.some(attachment => attachment.uploadFailed)
		},
	},
	watch: {
		newAttachments() {
			window.dispatchEvent(new CustomEvent('newAttachmentsUpdated', {detail: this.newAttachments}))
		},
		async canReachApi(canReachApi, previousCanReachApi) {
			if (canReachApi) return
			if (!previousCanReachApi) return
			await delay(1000)
			if (!this.isUploadingAttachmentFiles) return
			this.$notify.error({
				title: 'Upload paused',
				message: 'The connection has been interrupted. The upload will resume after reconnecting.',
			})
		},
	},
	methods: {
		async uploadAttachmentFiles(files) {
			const uploadAttachmentFilePromises = []
			for (const file of files) {
				const sizeInMb = file.size / 1024 / 1024
				const sizeLimitInMb = import.meta.env.VUE_APP_SESSION_ATTACHMENT_MAX_FILE_SIZE_MB || 20
				if (sizeInMb > sizeLimitInMb) {
					this.$notify.error({
						title: `Your file is too large`,
						message: `${file.name} exceeds limit. We support the upload of files up to a ${sizeLimitInMb}MB in size`,
					})
					continue
				}
				if (this.newAttachmentsByName[file.name]) continue
				this.newAttachmentsByName[file.name] = {
					id: file.name,
					name: file.name,
					isUploaded: false,
					uploadProgressPercentage: null,
					uploadFailed: false,
				}
				uploadAttachmentFilePromises.push(this.uploadAttachmentFile(file))
			}

			return Promise.all(uploadAttachmentFilePromises)
		},
		async uploadAttachmentFile(file, iteration = 1) {
			try {
				this.newAttachmentsByName[file.name] = {
					...this.newAttachmentsByName[file.name],
					uploadProgressPercentage: 0,
				}

				const formData = new FormData()
				formData.set('file', file)

				const onUploadProgress = progressEvent => {
					const uploadProgressPercentage = Math.ceil((progressEvent.loaded * 100) / progressEvent.total)
					if (!this.newAttachmentsByName[file.name]) return this.uploadRequest.abort()
					// When the internet connection is interrupted, progress jumps to 100% - we don't want that;
					// Completion can anyway be set to 100% when the request completed, this happens below;
					if (!navigator.onLine || uploadProgressPercentage === 100) return
					this.newAttachmentsByName[file.name] = {
						...this.newAttachmentsByName[file.name],
						uploadProgressPercentage,
					}
				}
				const response = await this.uploadAttachmentRequest(formData, onUploadProgress)
				const uploadedFileName = JSON.parse(response).name
				if (!this.newAttachmentsByName[file.name]) return
				this.newAttachmentsByName[file.name] = {
					...this.newAttachmentsByName[file.name],
					uploadedFileName,
					uploadProgressPercentage: 100,
					isUploaded: true,
				}
			} catch (error) {
				if (!this.newAttachmentsByName[file.name]) return
				// If upload failed due to network error, retry up to 3 times (after network comes back online or 10s of wait time);
				// Unfortunately `navigator.onLine` is only `false` in Chromium & Safari when there's not even a local network connection;
				if (error.code === 'ERR_NETWORK' && iteration < 4) {
					iteration++
					this.updateCanReachApi()
					if (this.canReachApi) {
						await delay(10000)
					} else {
						while (!this.canReachApi) {
							await delay(1000)
							this.updateCanReachApi()
						}
					}
					await this.uploadAttachmentFile(file, iteration)
					return
				}
				this.newAttachmentsByName[file.name] = {
					...this.newAttachmentsByName[file.name],
					uploadFailed: true,
				}
				this.$notify.error({
					title: `Some files failed to upload`,
					message: 'Please check your connection and try again, or contact support',
				})
				return
			}
		},
		async uploadAttachmentRequest(payload, onRequestProgress) {
			return new Promise((resolve, reject) => {
				const request = new XMLHttpRequest()
				request.withCredentials = true
				request.open(
					'POST',
					import.meta.env.VUE_APP_API + `/storage/upload-file?workspaceId=${this.$store.workspace.id}`
				)
				request.setRequestHeader(
					'X-Bird-Client',
					`${import.meta.env.VUE_APP_TARGET} v${import.meta.env.VUE_APP_VERSION}`
				)
				request.upload.addEventListener('progress', onRequestProgress, false)
				request.addEventListener(
					'loadend',
					function () {
						if (request.status === 200) resolve(request.response)
						// if error is returned from server
						else if (request.response) {
							const error = new Error(`Error during upload HTTP ${request.status}`)
							if (request.response) {
								const response = JSON.parse(request.response)
								error.name = response.error
								error.message = response.message
							}
							error.isBadRequest = String(request.status)[0] === '4'
							reject(error)
						}
						// if no error is returned from server it might be connectivity issue on the client side
						else {
							const error = new Error('Please check your internet connection.')
							reject(error)
						}
						request.removeEventListener('progress', onRequestProgress, false)
					},
					{once: true}
				)
				request.send(payload)
				this.uploadRequest = request
			})
		},
		async removeAttachment(attachment) {
			if (this.newAttachmentsByName[attachment.id]) {
				delete this.newAttachmentsByName[attachment.id]
			} else {
				this.removedAttachments.push(attachment)
			}
		},
		discardAttachmentChanges() {
			this.newAttachmentsByName = {}
			this.removedAttachments = []
		},
		async saveAttachmentChanges({sessionId, commentId}) {
			if (!this.newAttachments.length && !this.removedAttachments.length) return

			while (this.isUploadingAttachmentFiles) await delay(200)

			const newAttachments = this.newAttachments.filter(attachment => !attachment.uploadFailed)
			if (commentId) {
				const response = await this.$api.post(`comments/${commentId}/attachments`, {
					sessionId,
					attachments: newAttachments,
					removedAttachments: this.removedAttachments,
				})
				this.currentAttachments = response.commentAttachments
			} else {
				const response = await this.$api.post(`sessions/${sessionId}/attachments`, {
					attachments: newAttachments,
					removedAttachments: this.removedAttachments,
				})
				this.currentAttachments = response.sessionAttachments
			}
			this.discardAttachmentChanges()
			window.dispatchEvent(new CustomEvent('attachmentsUpdated'))
		},
	},
}

export default attachments
